import { useTranslate } from "@tolgee/react";
import type { RefObject } from "react";
import { cn } from "#/lib/utils";
import type { ProcessedLevel } from "./hooks/use-orderbook.ts";
import { formatOrderbookPrice } from "./lib/format-price.ts";
import { OrderbookRow } from "./orderbook-row.tsx";
import type {
	OrderbookSizeUnit,
	OrderbookViewMode,
} from "./types/orderbook.types.ts";

type OrderbookTableProps = {
	ref?: RefObject<HTMLDivElement | null>;
	coin: string;
	bids: ProcessedLevel[];
	asks: ProcessedLevel[];
	bidMarkers: ReadonlyMap<string, number>;
	askMarkers: ReadonlyMap<string, number>;
	spread: number;
	spreadPercent: string;
	lastTradedPrice: number;
	tradePriceArrow: string;
	tradePriceClassName: string;
	priceDecimals: number;
	viewMode: OrderbookViewMode;
	sizeUnit: OrderbookSizeUnit;
	onPriceSelect: (price: string) => void;
};

export function OrderbookTable({
	ref,
	coin,
	bids,
	asks,
	bidMarkers,
	askMarkers,
	spread,
	spreadPercent,
	lastTradedPrice,
	tradePriceArrow,
	tradePriceClassName,
	priceDecimals,
	viewMode,
	sizeUnit,
	onPriceSelect,
}: OrderbookTableProps) {
	const { t } = useTranslate();
	const showAsks = viewMode === "both" || viewMode === "asks";
	const showBids = viewMode === "both" || viewMode === "bids";
	const unitLabel = sizeUnit === "token" ? coin : "USDC";

	return (
		<>
			<div
				className={cn(
					"grid py-1.5 text-caption1-medium uppercase text-trade-text-faint",
					"grid-cols-[1fr_1fr] px-1.5 sm:grid-cols-[1fr_1fr_1fr] sm:px-3",
				)}
			>
				<div>
					<span className={"sm:hidden"}>{t("form.price")}</span>
					<span className={"hidden sm:block"}>
						{t("orderbook.columns.price")}
					</span>
				</div>
				<span className="text-right">
					{t("orderbook.columns.size", { unit: unitLabel })}
				</span>
				<span className="hidden text-right sm:block">
					{t("orderbook.columns.total", { unit: unitLabel })}
				</span>
			</div>

			<div ref={ref} className="flex min-h-0 flex-1 flex-col overflow-hidden">
				{showAsks && (
					<div
						className={`flex min-h-0 flex-1 flex-col overflow-hidden ${viewMode === "both" ? "justify-end" : ""}`}
					>
						{[...asks].reverse().map((level) => (
							<OrderbookRow
								key={`ask-${level.px}`}
								level={level}
								side="ask"
								coin={coin}
								markerCount={askMarkers.get(level.px) ?? 0}
								priceDecimals={priceDecimals}
								sizeUnit={sizeUnit}
								onPriceSelect={onPriceSelect}
							/>
						))}
					</div>
				)}

				{viewMode === "both" && (
					<div
						className={cn(
							"flex h-9 shrink-0 items-center justify-between border-y border-trade-border-subtle",
							"px-1.5 sm:px-3",
						)}
					>
						<div className="flex items-center gap-1.5">
							<span
								className={cn(
									"text-label1-tabular-bold sm:text-body1-bold transition-colors duration-300",
									tradePriceClassName,
								)}
							>
								{tradePriceArrow}{" "}
								{formatOrderbookPrice(lastTradedPrice, priceDecimals)}
							</span>
						</div>
						<div className="hidden flex-1 items-center justify-end gap-10 text-caption1 text-trade-text-faint sm:flex">
							<span>{t("orderbook.spread")}</span>
							<div className="flex items-center gap-x-2">
								<span className="text-label2-tabular-medium text-trade-text-secondary">
									{formatOrderbookPrice(spread, priceDecimals)}
								</span>
								<span className="text-label2-tabular-medium text-trade-text-secondary">
									{spreadPercent}%
								</span>
							</div>
						</div>
					</div>
				)}

				{showBids && (
					<div className="min-h-0 flex-1 overflow-hidden">
						{bids.map((level) => (
							<OrderbookRow
								key={`bid-${level.px}`}
								level={level}
								side="bid"
								coin={coin}
								markerCount={bidMarkers.get(level.px) ?? 0}
								priceDecimals={priceDecimals}
								sizeUnit={sizeUnit}
								onPriceSelect={onPriceSelect}
							/>
						))}
					</div>
				)}
			</div>
		</>
	);
}
